import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Image {
  imageUrl: string;
  id?: number;
}

interface PropertyImageGalleryProps {
  images: Image[];
  title: string;
}

export default function PropertyImageGallery({ images, title }: PropertyImageGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Function to open the lightbox with a specific image
  const openLightbox = (index: number) => {
    setLightboxImageIndex(index);
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling while lightbox is open
  };

  // Function to close the lightbox
  const closeLightbox = () => {
    setIsLightboxOpen(false);
    document.body.style.overflow = ''; // Re-enable scrolling
  };

  // Navigate to previous image in lightbox
  const prevLightboxImage = () => {
    setLightboxImageIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  };

  // Navigate to next image in lightbox
  const nextLightboxImage = () => {
    setLightboxImageIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          prevLightboxImage();
          break;
        case 'ArrowRight':
          nextLightboxImage();
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen]);

  // Scroll to the selected thumbnail
  useEffect(() => {
    if (scrollContainerRef.current && images.length > 1) {
      const container = scrollContainerRef.current;
      const scrollPosition = 100 * currentImageIndex; // 100px is the width of each thumbnail + gap
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentImageIndex]);

  // Handle scroll left
  const scrollLeft = () => {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = prevIndex === 0 ? 0 : prevIndex - 1;
      return newIndex;
    });
  };

  // Handle scroll right
  const scrollRight = () => {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = prevIndex === images.length - 1 ? images.length - 1 : prevIndex + 1;
      return newIndex;
    });
  };

  if (!images || images.length === 0) {
    return (
      <div className="aspect-video rounded-lg overflow-hidden bg-muted mb-4 flex items-center justify-center">
        <p className="text-muted-foreground">No images available</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {/* Main Featured Image */}
      <div 
        className="aspect-video rounded-lg overflow-hidden bg-muted mb-4 cursor-pointer relative"
        onClick={() => openLightbox(currentImageIndex)}
      >
        <img 
          src={images[currentImageIndex].imageUrl} 
          alt={`${title} - Featured image`} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-opacity duration-300 flex items-center justify-center">
          <span className="text-transparent hover:text-white transition-colors opacity-0 hover:opacity-100 font-medium">
            Click to view
          </span>
        </div>
      </div>
      
      {/* Thumbnails with Scrolling */}
      {images.length > 1 && (
        <div className="relative">
          <div 
            className="flex space-x-2 overflow-x-auto scrollbar-hide py-2 px-1"
            ref={scrollContainerRef}
          >
            {images.map((image, index) => (
              <div 
                key={index} 
                className={cn(
                  "flex-shrink-0 w-24 h-24 rounded-md overflow-hidden cursor-pointer transition-all duration-200 hover:opacity-90",
                  currentImageIndex === index ? "ring-2 ring-primary ring-offset-2" : "opacity-70"
                )}
                onClick={() => {
                  setCurrentImageIndex(index);
                }}
              >
                <img 
                  src={image.imageUrl} 
                  alt={`${title} - ${index + 1}`} 
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          
          {/* Navigation buttons */}
          {images.length > 4 && (
            <>
              <Button 
                size="icon"
                variant="outline"
                className={cn(
                  "absolute left-0 top-1/2 transform -translate-y-1/2 z-10 rounded-full w-8 h-8 p-0 bg-white/80 hover:bg-white",
                  currentImageIndex === 0 ? "opacity-50 cursor-not-allowed" : "opacity-90"
                )}
                onClick={scrollLeft}
                disabled={currentImageIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button 
                size="icon"
                variant="outline"
                className={cn(
                  "absolute right-0 top-1/2 transform -translate-y-1/2 z-10 rounded-full w-8 h-8 p-0 bg-white/80 hover:bg-white",
                  currentImageIndex === images.length - 1 ? "opacity-50 cursor-not-allowed" : "opacity-90"
                )}
                onClick={scrollRight}
                disabled={currentImageIndex === images.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}
      
      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
          <div className="relative max-w-6xl w-full max-h-[90vh] px-4">
            {/* Close button */}
            <Button
              variant="outline"
              size="icon"
              className="absolute top-2 right-2 z-20 bg-black/50 hover:bg-black/70 text-white border-none"
              onClick={closeLightbox}
            >
              <X className="h-6 w-6" />
            </Button>
            
            {/* Navigation buttons */}
            <Button
              variant="outline"
              size="icon"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 rounded-full bg-black/50 hover:bg-black/70 text-white border-none"
              onClick={prevLightboxImage}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 rounded-full bg-black/50 hover:bg-black/70 text-white border-none"
              onClick={nextLightboxImage}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
            
            {/* Current image */}
            <div className="w-full h-[80vh] flex items-center justify-center">
              <img 
                src={images[lightboxImageIndex].imageUrl} 
                alt={`${title} - ${lightboxImageIndex + 1}`} 
                className="max-w-full max-h-full object-contain"
              />
            </div>
            
            {/* Indicator */}
            <div className="absolute bottom-4 left-0 right-0 text-center text-white">
              {lightboxImageIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}